import React from 'react'

import ColumnLegendButton from './ColumnLegendButton'
import { TAG_CATEGORIES, TAG_DEFINITIONS, tagBadgeClass } from './types'
import { activeFilterCount, CANONICAL_CHROMS, type FacetCounts, type VariantFilters } from './variantQuery'

const STATIC_TAG_LEGEND_ITEMS = TAG_CATEGORIES.map((tag) => ({ label: tag, help: TAG_DEFINITIONS[tag] }))

export type FilterSidebarProps = {
  filters: VariantFilters
  facets: FacetCounts
  onChange: (next: VariantFilters) => void
  onReset: () => void
  /** True while more pages are still loading, so counts are labeled partial. */
  partial: boolean
  /** True when `facets` are whole-case totals (from the server), not page-local. */
  authoritative?: boolean
  /**
   * Header-derived INFO/TAG vocabulary (ordered) for the current track. Drives
   * the TAG list so each VCF surfaces its own categories instead of a fixed
   * client-side set. Empty falls back to whatever tags appear in `facets`.
   */
  tagOrder?: string[]
  /**
   * Definitions for the current track's TAG vocabulary (SNV or CNV), from the
   * API. When present the legend uses these so the CNV track shows CNV wording;
   * empty falls back to the static SNV definitions.
   */
  tagDefinitions?: Record<string, string>
}

function Section({
  title,
  count,
  legend,
  children,
}: {
  title: string
  count?: number
  legend?: React.ReactNode
  children: React.ReactNode
}) {
  return (
    <div className="border-border-subtle border-b py-3">
      <div className="mb-2 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <h3 className="text-text-subtle text-xs font-semibold tracking-wide uppercase">{title}</h3>
          {legend}
        </div>
        {count ? (
          <span className="bg-brand-100 text-brand-800 rounded-full px-1.5 text-[10px] font-medium">{count}</span>
        ) : null}
      </div>
      {children}
    </div>
  )
}

function sortedKeys(counts: Record<string, number>): string[] {
  return Object.keys(counts).sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
}

const FilterSidebar: React.FC<FilterSidebarProps> = ({
  filters,
  facets,
  onChange,
  onReset,
  partial,
  authoritative = false,
  tagOrder = [],
  tagDefinitions = {},
}) => {
  const toggle = (list: string[], value: string): string[] =>
    list.includes(value) ? list.filter((x) => x !== value) : [...list, value]

  const numberOrNull = (raw: string): number | null => (raw.trim() === '' ? null : Number(raw))

  // Legend: when the API returned per-track definitions (e.g. the CNV track),
  // build the legend from that ordered vocabulary so the wording matches the
  // active track; otherwise keep the static SNV legend.
  const hasApiDefinitions = Object.keys(tagDefinitions).length > 0
  const tagLegendItems = hasApiDefinitions
    ? (tagOrder.length > 0 ? tagOrder : Object.keys(tagDefinitions))
        .filter((tag) => tag in tagDefinitions)
        .map((tag) => ({ label: tag, help: tagDefinitions[tag] }))
    : STATIC_TAG_LEGEND_ITEMS

  // Drive the TAG list from the VCF's header-derived vocabulary (tagOrder) so a
  // new VCF surfaces its own categories instead of a fixed client-side set; then
  // append any extra tags present in the counts but not declared in the header.
  // When the vocabulary is unavailable (e.g. a CNV track with no TAG, or the
  // facets request failed) fall back to whatever the counts contain.
  const extraTags = Object.keys(facets.tags)
    .filter((t) => !tagOrder.includes(t))
    .sort()
  const tagKeys = [...tagOrder, ...extraTags]

  // Always offer the canonical contigs (so chr2+ is reachable before its rows
  // load), then append any non-canonical contigs that did show up (alt/decoy).
  const extraChroms = Object.keys(facets.chroms)
    .filter((c) => !CANONICAL_CHROMS.includes(c))
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
  const chromKeys = [...CANONICAL_CHROMS, ...extraChroms]

  return (
    <aside className="flex h-full flex-col overflow-auto p-4" data-testid="filter-sidebar">
      <div className="mb-2 flex items-center justify-between">
        <h2 className="text-text-heading text-sm font-semibold">
          Filters {activeFilterCount(filters) > 0 ? `(${activeFilterCount(filters)})` : ''}
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="text-brand-700 text-xs hover:underline"
          data-testid="filter-reset"
        >
          Reset
        </button>
      </div>
      {partial && !authoritative && (
        <p className="text-text-subtle mb-2 text-[11px]">
          Counts reflect loaded variants; scroll the table to load more.
        </p>
      )}

      <Section
        title="TAG"
        count={filters.tags.length}
        legend={
          <ColumnLegendButton
            items={tagLegendItems}
            heading="Tag definitions"
            ariaLabel="Tag definitions"
            title="What do these tags mean?"
            testId="tag-legend"
            labelWidth={128}
            footer="Definitions are provisional and subject to change."
          />
        }
      >
        <div className="flex flex-col gap-1">
          {tagKeys.map((key) => {
            const count = facets.tags[key] ?? 0
            // Dim (but keep selectable) tags with no loaded variants, so the
            // full vocabulary is visible without implying they have data.
            const muted = count === 0 && !filters.tags.includes(key)
            return (
              <label
                key={key}
                className={`flex cursor-pointer items-center gap-2 text-xs ${muted ? 'opacity-50' : ''}`}
              >
                <input
                  type="checkbox"
                  checked={filters.tags.includes(key)}
                  onChange={() => onChange({ ...filters, tags: toggle(filters.tags, key) })}
                  data-testid={`filter-tag-${key}`}
                />
                <span className={`truncate rounded px-1.5 py-0.5 text-[10px] font-medium ${tagBadgeClass(key)}`}>
                  {key}
                </span>
                <span className="text-text-subtle ml-auto tabular-nums">{count}</span>
              </label>
            )
          })}
          {tagKeys.length === 0 && <span className="text-text-subtle text-xs">-</span>}
        </div>
      </Section>

      <Section title="FILTER" count={filters.filters.length}>
        <div className="flex flex-col gap-1">
          {sortedKeys(facets.filters).map((key) => (
            <label key={key} className="flex cursor-pointer items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={filters.filters.includes(key)}
                onChange={() => onChange({ ...filters, filters: toggle(filters.filters, key) })}
              />
              <span className="font-mono">{key || '(empty)'}</span>
              <span className="text-text-subtle ml-auto tabular-nums">{facets.filters[key]}</span>
            </label>
          ))}
          {Object.keys(facets.filters).length === 0 && <span className="text-text-subtle text-xs">-</span>}
        </div>
      </Section>

      <Section title="Chromosome" count={filters.chroms.length}>
        <p className="text-text-subtle mb-1.5 text-[11px]">Pick one to load that whole chromosome.</p>
        <div className="flex flex-wrap gap-1">
          {chromKeys.map((chrom) => {
            const active = filters.chroms.includes(chrom)
            const count = facets.chroms[chrom]
            return (
              <button
                key={chrom}
                type="button"
                title={
                  count !== undefined
                    ? authoritative
                      ? `${count} variants`
                      : `${count} loaded`
                    : 'Load this chromosome'
                }
                // Single-select: picking a chromosome scopes the server-side
                // fetch to it; clicking the active one clears the scope.
                onClick={() => onChange({ ...filters, chroms: active ? [] : [chrom] })}
                className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${
                  active ? 'bg-brand-600 text-white' : 'text-text-subtle hover:bg-brand-50 bg-gray-100'
                }`}
              >
                {chrom}
              </button>
            )
          })}
        </div>
      </Section>

      <Section title="QUAL (min)">
        <input
          type="number"
          inputMode="decimal"
          value={filters.minQual ?? ''}
          placeholder="any"
          onChange={(e) => onChange({ ...filters, minQual: numberOrNull(e.target.value) })}
          className="border-border-subtle w-full rounded border px-2 py-1 text-xs"
          data-testid="filter-minqual"
        />
      </Section>

      <Section title="Proband VAF range">
        <div className="flex items-center gap-2">
          <input
            type="number"
            step="0.05"
            min={0}
            max={1}
            value={filters.vafMin}
            onChange={(e) => onChange({ ...filters, vafMin: Math.min(Math.max(Number(e.target.value), 0), 1) })}
            className="border-border-subtle w-full rounded border px-2 py-1 text-xs"
            aria-label="VAF min"
          />
          <span className="text-text-subtle text-xs">to</span>
          <input
            type="number"
            step="0.05"
            min={0}
            max={1}
            value={filters.vafMax}
            onChange={(e) => onChange({ ...filters, vafMax: Math.min(Math.max(Number(e.target.value), 0), 1) })}
            className="border-border-subtle w-full rounded border px-2 py-1 text-xs"
            aria-label="VAF max"
          />
        </div>
      </Section>

      <Section title="Proband DP (min)">
        <input
          type="number"
          inputMode="numeric"
          value={filters.minDp ?? ''}
          placeholder="any"
          onChange={(e) => onChange({ ...filters, minDp: numberOrNull(e.target.value) })}
          className="border-border-subtle w-full rounded border px-2 py-1 text-xs"
          data-testid="filter-mindp"
        />
      </Section>
    </aside>
  )
}

export default FilterSidebar
